<template>
  <el-container class="admin-layout">
    <el-aside :width="isCollapse ? '64px' : '220px'" class="aside-container">
      <div class="logo">
        <h1>{{ isCollapse ? '管理' : '后台管理系统' }}</h1>
      </div>
      <el-menu
        :router="true"
        :default-active="$route.path"
        class="el-menu-vertical"
        :collapse="isCollapse"
        background-color="#304156"
        text-color="#fff"
        active-text-color="#409EFF"
      >
        <el-menu-item index="/admin/users">
          <el-icon><User /></el-icon>
          <template #title>用户管理</template>
        </el-menu-item>
        
        <el-sub-menu index="news">
          <template #title>
            <el-icon><Document /></el-icon>
            <span>新闻管理</span>
          </template>
          <el-menu-item index="/admin/news-categories">
            <el-icon><Collection /></el-icon>
            <span>新闻分类</span>
          </el-menu-item>
          <el-menu-item index="/admin/news">
            <el-icon><List /></el-icon>
            <span>新闻列表</span>
          </el-menu-item>
        </el-sub-menu>

        <el-menu-item index="/admin/menus">
          <el-icon><Menu /></el-icon>
          <template #title>导航菜单</template>
        </el-menu-item>

        <el-menu-item index="/admin/works">
          <el-icon><Picture /></el-icon>
          <template #title>作品管理</template>
        </el-menu-item>

        <el-sub-menu index="forms">
          <template #title>
            <el-icon><Tickets /></el-icon>
            <span>表单管理</span>
          </template>
          <el-menu-item index="/admin/forms">
            <el-icon><List /></el-icon>
            <span>表单列表</span>
          </el-menu-item>
          <!-- 动态表单子菜单 -->
          <template v-if="formMenus.length > 0">
            <el-sub-menu v-for="form in formMenus" :key="form.id" :index="'form-' + form.id">
              <template #title>
                <el-icon><Document /></el-icon>
                <span>{{ form.title }}</span>
              </template>
              <el-menu-item :index="'/admin/forms/' + form.id + '/submit'">
                <el-icon><Edit /></el-icon>
                <span>提交表单</span>
              </el-menu-item>
              <el-menu-item :index="'/admin/forms/' + form.id + '/submissions'">
                <el-icon><List /></el-icon>
                <span>提交记录</span>
              </el-menu-item>
            </el-sub-menu>
          </template>
        </el-sub-menu>
      </el-menu>
    </el-aside>
    
    <el-container class="main-container">
      <el-header class="header">
        <div class="header-left">
          <el-button
            type="text"
            class="collapse-btn"
            @click="toggleCollapse"
          >
            <el-icon :size="20">
              <component :is="isCollapse ? 'Expand' : 'Fold'" />
            </el-icon>
          </el-button>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/admin' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item>{{ currentMenu }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <div class="lang-switch">
            <span class="lang-label">语言：</span>
            <el-radio-group v-model="currentLanguage" size="small" @change="handleLanguageChange">
              <el-radio-button label="simplified">简体</el-radio-button>
              <el-radio-button label="traditional">繁體</el-radio-button>
            </el-radio-group>
          </div>
          <el-dropdown @command="handleCommand">
            <div class="user-info">
              <el-avatar :size="32" class="user-avatar">
                {{ authStore.user?.username?.charAt(0)?.toUpperCase() }}
              </el-avatar>
              <span class="username">{{ authStore.user?.username || '管理员' }}</span>
              <el-icon class="el-icon--right"><arrow-down /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      
      <el-main class="main-content">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  User, ArrowDown, Document, Collection, List,
  Menu as MenuIcon, Picture, Tickets, Expand, Fold,
  SwitchButton, Edit
} from '@element-plus/icons-vue'
import { useAuthStore } from '../stores/auth'
import { useRouter, useRoute } from 'vue-router'
import { useLangStore } from '../stores/lang'
import { userApi } from '@/api/users'
import { formApi } from '@/api/forms'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const isCollapse = ref(false)
const langStore = useLangStore()
const formMenus = ref([])

// 获取活动的表单列表用于生成菜单
const fetchFormMenus = async () => {
  try {
    const res = await formApi.getForms({ is_active: true })
    formMenus.value = res.data.results || []
  } catch (error) {
    console.error('获取表单列表失败:', error)
  }
}

// 计算当前菜单名称
const currentMenu = computed(() => {
  const pathMap = {
    '/admin/users': '用户管理',
    '/admin/news-categories': '新闻分类',
    '/admin/news': '新闻列表',
    '/admin/news/create': '新建新闻',
    '/admin/news/edit': '编辑新闻',
    '/admin/menus': '导航菜单',
    '/admin/works': '作品管理',
    '/admin/forms': '表单管理',
    '/admin/forms/create': '新建表单',
    '/admin/forms/edit': '编辑表单'
  }

  // 处理动态表单路由
  if (route.params.id) {
    const form = formMenus.value.find(f => f.id === parseInt(route.params.id))
    if (form) {
      if (route.path.includes('/submit')) {
        return `${form.title} - 提交表单`
      }
      if (route.path.includes('/submissions')) {
        return `${form.title} - 提交记录`
      }
    }
  }

  return pathMap[route.path] || '首页'
})

onMounted(() => {
  fetchFormMenus()
})

const toggleCollapse = () => {
  isCollapse.value = !isCollapse.value
}

const handleCommand = (command) => {
  if (command === 'logout') {
    userApi.logout()
    router.push('/login')
  }
}

const currentLanguage = ref(langStore.currentLang)
const handleLanguageChange = () => {
  langStore.switchLang(currentLanguage.value)
}
</script>

<style scoped lang="scss">
.admin-layout {
  height: 100vh;
  
  .aside-container {
    transition: width 0.3s;
    background-color: #304156;
    box-shadow: 2px 0 6px rgba(0, 21, 41, 0.15);
    z-index: 999;
    overflow: hidden;
    
    .logo {
      height: 60px;
      line-height: 60px;
      text-align: center;
      background-color: #2b3649;
      overflow: hidden;
      
      h1 {
        color: #fff;
        font-size: 18px;
        margin: 0;
        white-space: nowrap;
        transition: all 0.3s;
      }
    }
    
    .el-menu {
      border-right: none;
      
      &:not(.el-menu--collapse) {
        width: 220px;
      }
    }
  }
  
  .main-container {
    .header {
      background-color: #fff;
      border-bottom: 1px solid #dcdfe6;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 20px;
      box-shadow: 0 1px 4px rgba(0,21,41,0.08);
      
      .header-left {
        display: flex;
        align-items: center;
        
        .collapse-btn {
          padding: 0;
          margin-right: 20px;
          
          &:hover {
            background: transparent;
          }
          
          .el-icon {
            color: #303133;
          }
        }
      }
      
      .header-right {
        display: flex;
        align-items: center;
        
        .lang-switch {
          display: flex;
          align-items: center;
          position: static;
          margin-right: 15px;          
          .lang-label {
            margin-right: 8px;
            font-size: 14px;
            color: #606266;
          }
          
          :deep(.el-radio-button__inner) {
            padding: 5px 12px;
          }
        }
        
        .user-info {
          display: flex;
          align-items: center;
          cursor: pointer;
          padding: 0 8px;
          height: 60px;
          
          &:hover {
            background: rgba(0,0,0,0.025);
          }
          
          .user-avatar {
            background: #409EFF;
            color: #fff;
            margin-right: 8px;
          }
          
          .username {
            font-size: 14px;
            color: #303133;
            margin-right: 4px;
          }
        }
      }
    }
    
    .main-content {
      background-color: #f0f2f5;
      padding: 20px;
      
      :deep(.el-card) {
        margin-bottom: 20px;
      }
    }
  }
}

// 页面切换动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.el-menu-item) {
  &.is-active {
    background-color: #263445 !important;
  }
  
  &:hover {
    background-color: #263445 !important;
  }
}

:deep(.el-sub-menu) {
  .el-menu-item {
    background-color: #1f2d3d !important;
    
    &.is-active {
      background-color: #001528 !important;
    }
    
    &:hover {
      background-color: #001528 !important;
    }
  }
}
</style> 